import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getProjects, createProject, getTrashProjects, restoreProject, permanentDeleteProject, type Project } from "@/lib/projectStore";
import { seedCCBNicheProject } from "@/lib/seedCCBExample";
import { seedHRMSProject } from "@/lib/seedHRMSProject";
import { useAuth } from "../context/AuthContext";
import ProjectCard from "@/components/ProjectCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, FolderOpen, Trash2, RotateCcw, X, Sparkles, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [open, setOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedingHRMS, setSeedingHRMS] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [methodology, setMethodology] = useState<"agile" | "waterfall">("agile");
  const [loading, setLoading] = useState(true);
  const [trashOpen, setTrashOpen] = useState(false);
  const [trash, setTrash] = useState<(Project & { deleted_at: string })[]>([]);
  const [trashLoading, setTrashLoading] = useState(false);

  const refresh = async () => {
    const data = await getProjects();
    setProjects(data);
    setLoading(false);
  };

  const loadTrash = async () => {
    setTrashLoading(true);
    const data = await getTrashProjects();
    setTrash(data);
    setTrashLoading(false);
  };

  const handleRestore = async (id: string) => {
    await restoreProject(id);
    await Promise.all([loadTrash(), refresh()]);
  };

  const handlePermanentDelete = async (id: string) => {
    await permanentDeleteProject(id);
    await loadTrash();
  };

  const toggleTrash = () => {
    setTrashOpen(v => !v);
    if (!trashOpen) loadTrash();
  };

  useEffect(() => { refresh(); loadTrash(); }, []);

  const handleCreate = async () => {
    if (!name.trim()) return;
    await createProject(name.trim(), desc.trim());
    setName("");
    setDesc("");
    setMethodology("agile");
    setOpen(false);
    refresh();
  };

  const handleSeedCCB = async () => {
    if (!token) return;
    setSeeding(true);
    try {
      const projectId = await seedCCBNicheProject(token);
      await refresh();
      toast.success("CCB example project loaded");
      navigate(`/studio/project/${projectId}`);
    } catch {
      toast.error("Failed to load example project");
    } finally {
      setSeeding(false);
    }
  };

  const handleSeedHRMS = async () => {
    if (!token) return;
    setSeedingHRMS(true);
    try {
      const projectId = await seedHRMSProject(token);
      await refresh();
      toast.success("HRMS example project loaded — all tabs populated");
      navigate(`/studio/project/${projectId}`);
    } catch {
      toast.error("Failed to load HRMS example project");
    } finally {
      setSeedingHRMS(false);
    }
  };

  const activeProjects = projects.filter((p) => p.status === "active");
  const draftProjects = projects.filter((p) => p.status === "draft");
  const archivedProjects = projects.filter((p) => p.status === "archived");

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">
            Start Here
          </h1>
          <p className="text-sm text-muted-foreground font-body mt-1">
            Create your first project and explore the full BA workflow
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={toggleTrash} className="gap-1.5 text-muted-foreground">
            <Trash2 className="h-4 w-4" /> Trash{trash.length > 0 && !trashOpen ? ` (${trash.length})` : ""}
          </Button>
          <Button size="sm" variant="outline" onClick={handleSeedCCB} disabled={seeding} className="gap-1.5 text-violet-600 border-violet-200 hover:bg-violet-50">
            <Sparkles className="h-4 w-4" />
            {seeding ? "Loading…" : "Load CCB Example"}
          </Button>
          <Button size="sm" variant="outline" onClick={handleSeedHRMS} disabled={seedingHRMS} className="gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50">
            <Building2 className="h-4 w-4" />
            {seedingHRMS ? "Loading…" : "Load HRMS Example"}
          </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5">
              <Plus className="h-4 w-4" /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">New Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <Input
                placeholder="Project name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              />
              <Textarea
                placeholder="Brief description (optional)"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                rows={2}
              />
              <div>
                <label className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1.5 block">
                  Methodology
                </label>
                <Select value={methodology} onValueChange={(v) => setMethodology(v as any)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agile">Agile (Sprints)</SelectItem>
                    <SelectItem value="waterfall">Waterfall</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleCreate} className="w-full">
                Create Project
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Trash panel */}
      {trashOpen && (
        <div className="mb-6 border border-destructive/30 rounded-md bg-destructive/5 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-mono text-destructive uppercase tracking-wider flex items-center gap-1.5">
              <Trash2 className="h-3.5 w-3.5" /> Trash — deleted projects (auto-purged after 30 days)
            </h2>
            <button onClick={() => setTrashOpen(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
          {trashLoading && <p className="text-xs text-muted-foreground animate-pulse">Loading…</p>}
          {!trashLoading && trash.length === 0 && (
            <p className="text-xs text-muted-foreground">Trash is empty.</p>
          )}
          {!trashLoading && trash.map(p => (
            <div key={p.id} className="flex items-center justify-between border border-border rounded-md bg-background px-3 py-2">
              <div>
                <p className="text-sm font-semibold text-foreground">{p.name}</p>
                <p className="text-[10px] text-muted-foreground">
                  Deleted {new Date(p.deleted_at).toLocaleString()}
                  {p.description ? ` · ${p.description.slice(0, 60)}` : ""}
                </p>
              </div>
              <div className="flex gap-2 shrink-0 ml-4">
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => handleRestore(p.id)}>
                  <RotateCcw className="h-3 w-3" /> Restore
                </Button>
                <Button size="sm" variant="destructive" className="h-7 text-xs gap-1" onClick={() => handlePermanentDelete(p.id)}>
                  <X className="h-3 w-3" /> Delete forever
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center py-20 text-sm text-muted-foreground">Loading projects…</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-20 animate-fade-in">
          <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
          <h2 className="font-display text-lg font-semibold text-foreground mb-1">
            No projects yet
          </h2>
          <p className="text-sm text-muted-foreground mb-6 font-body">
            Create your first BA project to start documenting requirements and flows
          </p>
          <Button onClick={() => setOpen(true)} size="sm" className="gap-1.5">
            <Plus className="h-4 w-4" /> Create Project
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {activeProjects.length > 0 && (
            <ProjectSection title="Active" projects={activeProjects} onDeleted={refresh} />
          )}
          {draftProjects.length > 0 && (
            <ProjectSection title="Drafts" projects={draftProjects} onDeleted={refresh} />
          )}
          {archivedProjects.length > 0 && (
            <ProjectSection title="Archived" projects={archivedProjects} onDeleted={refresh} />
          )}
        </div>
      )}
    </div>
  );
}

function ProjectSection({ title, projects, onDeleted }: { title: string; projects: Project[]; onDeleted: () => void }) {
  return (
    <div>
      <h2 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-3">
        {title} ({projects.length})
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((p) => (
          <ProjectCard key={p.id} project={p} onDeleted={onDeleted} />
        ))}
      </div>
    </div>
  );
}
